import {
  Button,
  DropdownMenu,
  Input,
  Label,
  TableItem,
  TableMessage,
} from 'components/atoms';
import { FormInput } from 'components/molecules';
import DigitalDeliveryBottomBar, {
  isDeliveryWithdrawnStatus,
} from 'components/molecules/digital-delivery-bottom-bar/DigitalDeliveryBottomBar';
import LabeledDeliveryStatus from 'components/molecules/labeledStatus/LabeledDeliveryStatus';
import Popup from 'components/molecules/popup';
import DeliveryDiscrepancyPopup from 'components/molecules/popup/delivery-discrepancy/DeliveryDiscrepancyPopup';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { useComponentVisible } from 'hooks';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import i18next from 'i18next';
import middleware from 'middleware';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PiCaretLeft,
  PiCheck,
  PiDotsThreeVerticalBold,
  PiGlobeSimple,
  PiPlus,
  PiTrash,
  PiX,
  PiXCircle,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useAcceptAllDigitalDelivery,
  useCancelAllDigitalDelivery,
  usePostAllDigitalDeliveries,
  usePutAllDigitalDeliveries,
  useWithdrawnAllDigitalDelivery,
} from 'services/digital-delivery';
import {
  AsapType,
  DELIVERY_METHOD,
  ICreateAllDigitalDeliveries,
  IDigitalDeliveryBase,
  TRANSFER_METHOD,
  YT_CONTENT_MATCH_POLICY,
} from 'services/digital-delivery/types';
import { useGetCountries } from 'services/enums';
import { useGetMusicProductDigitalDeliveryInformation } from 'services/music-product-digital/delivery';
import {
  DELIVERY_STATUS,
  SUBSCRIPTION_TYPE,
} from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { useGetDeliveryTemplate } from 'services/settings/digital-delivery-template';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { formatDateToInput } from 'utils/global/date.utils';
import { requiredEnumSelect } from 'utils/global/validation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { array, boolean, object, string } from 'yup';

const digitalDeliveryInitialValues = (
  publishDate?: string,
  template?: {
    countryCode: string;
    subscriptionTypeId: number;
    asap: number;
    isTimeZoneBased: boolean;
  } | null
): IDigitalDeliveryBase => ({
  asap: (template?.asap ?? AsapType.None) as AsapType,
  isTimeZoneBased: template?.isTimeZoneBased ?? false,
  countryCode: template?.countryCode ?? '',
  publishDate: publishDate ?? formatDateToInput(new Date()),
  publishTime: '00:00',
  subscriptionTypeId: template?.subscriptionTypeId ?? SUBSCRIPTION_TYPE.all,
  endDate: null,
  endTime: null,
});

const digitalDeliveryBaseValidator = object().shape({
  asap: requiredEnumSelect(AsapType),
  isTimeZoneBased: boolean().required(i18next.t('formik-error.required')),
  countryCode: string().required(i18next.t('formik-error.required')),
  publishDate: string().when('asap', {
    is: (asap: AsapType) => asap !== AsapType.Asap,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().optional(),
  }),
  publishTime: string().when('asap', {
    is: (asap: AsapType) => asap !== AsapType.Asap,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().optional(),
  }),
  subscriptionTypeId: requiredEnumSelect(SUBSCRIPTION_TYPE),
});

export const digitalDeliveryValidator = object().shape({
  included: array().of(digitalDeliveryBaseValidator),
  excluded: array().of(digitalDeliveryBaseValidator),
});

const MusicProductDigitalDeliveryAll = () => {
  const productId = useCustomParamId('id', '/music-product-digital');

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { getSelectOptions } = useEnumToSelectOptions();

  const {
    values,
    setFieldValue,
    validateField,
    errors,
  }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  usePermissionGuard('DigitalDeliveryPermissionsLIST**');

  const minPublishDate =
    new Date(values.releaseDate).getTime() > new Date().getTime()
      ? formatDateToInput(values.releaseDate)
      : formatDateToInput(new Date());

  const digitalDeliveryCosistent = useGetMusicProductDigitalDeliveryInformation(
    productId,
    false
  );

  const createAllDigitalDeliveries = usePostAllDigitalDeliveries();
  const updateAllDigitalDeliveries = usePutAllDigitalDeliveries();
  const cancelAllDigitalDelivery = useCancelAllDigitalDelivery();
  const acceptAllDigitalDelivery = useAcceptAllDigitalDelivery();
  const withdrawnAllDigitalDelivery = useWithdrawnAllDigitalDelivery();

  const { data: countries } = useGetCountries();
  const { data: template } = useGetDeliveryTemplate();

  useEffect(() => {
    if (digitalDeliveryCosistent.data && values.mainTitle.title) {
      const mapped = digitalDeliveryCosistent.data.delivery
        ? middleware.digitalDelivery.queryToForm(
            digitalDeliveryCosistent.data.delivery,
          )
        : {
            priority: false,
            included: [],
            excluded: [],
            isReconnection: false,
          };
      if (
        digitalDeliveryCosistent.data.insert &&
        mapped.included?.length === 0 &&
        mapped.excluded?.length === 0 &&
        template &&
        (template.included?.length || template.excluded?.length)
      ) {
        const mapFromTemplate = (arr: typeof template.included) =>
          (arr ?? []).map(ti =>
            digitalDeliveryInitialValues(minPublishDate, ti)
          );
        setFieldValue('delivery', {
          ...mapped,
          included: mapFromTemplate(template.included),
          excluded: mapFromTemplate(template.excluded),
        });
      } else {
        setFieldValue('delivery', mapped);
      }
    }
  }, [
    digitalDeliveryCosistent.data,
    values.mainTitle.title,
    setFieldValue,
    template,
    minPublishDate,
  ]);

  const onSubmitDelivery = async (actions?: PopupActions) => {
    validateField('delivery');

    if (Object.keys(errors?.delivery ?? {}).length) {
      return toast.error(t('error-code.FORM_ERROR'));
    }

    if (values.delivery) {
      const deliveryIdentification = { productId };

      const delivery: ICreateAllDigitalDeliveries = {
        ...deliveryIdentification,
        included: values.delivery.included.map(d => ({
          ...d,
          publishTime: `${d.publishTime}:00`.slice(0, 8),
        })),
        excluded: values.delivery.excluded.map(d => ({
          ...d,
          publishTime: `${d.publishTime}:00`.slice(0, 8),
        })),
        priority: false,
        isReconnection: values.delivery.isReconnection ?? false,
        ytContentMatchPolicy: values.delivery.requireYtContentMatchPolicy
          ? values.delivery.ytContentMatchPolicy ?? null
          : null,
      };

      return digitalDeliveryCosistent.data?.insert
        ? await createAllDigitalDeliveries.mutateAsync(delivery, {
            onSuccess: async () => {
              await digitalDeliveryCosistent.refetch();
              // navigate(`/music-product-digital/${productId}/delivery`);
              actions?.close();
            },
            onError: async error => onErrorShowAllErrorsToast(error),
          })
        : await updateAllDigitalDeliveries.mutateAsync(delivery, {
            onSuccess: async () => {
              await digitalDeliveryCosistent.refetch();
              // navigate(`/music-product-digital/${productId}/delivery`);
              actions?.close();
            },
            onError: async error => onErrorShowAllErrorsToast(error),
          });
    }
  };

  const getDeliveryNodes = (deliveries?: IDigitalDeliveryBase[]) => ({
    nodes: deliveries?.map((d, key) => ({ ...d, id: key })) ?? [],
  });

  const onAddDigitalDelivery = (prefix: 'included' | 'excluded') => {
    if (!values.delivery) return;

    setFieldValue(
      `delivery.${prefix}[${values.delivery[prefix].length}]`,
      digitalDeliveryInitialValues(minPublishDate, null)
    );
  };

  const onRemoveDigitalDelivery = (
    id: number,
    prefix: 'included' | 'excluded'
  ) => {
    values.delivery &&
      setFieldValue(
        `delivery.${prefix}`,
        values.delivery[prefix].filter((d, i) => i !== id)
      );
  };

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  const isDeliveryMethodManual =
    values.delivery?.deliveryType === DELIVERY_METHOD.Manual;

  const isDeliveryCanceled =
    values.delivery?.status === DELIVERY_STATUS.canceled;

  const isDeliveryWithdrawn = isDeliveryWithdrawnStatus(values.delivery?.status);

  const cancelVisible = useComponentVisible<HTMLDivElement>(false);

  const onCancelDelivery = async (actions: PopupActions) => {
    await cancelAllDigitalDelivery.mutateAsync(
      {
        productId,
      },
      {
        onSuccess: async () => {
          await digitalDeliveryCosistent.refetch();
          actions.close();
        },
        onError: async error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onWithdrawnDelivery = async (
    actions: PopupActions,
    priority: boolean
  ) => {
    await withdrawnAllDigitalDelivery.mutateAsync(
      {
        productId,
        priority,
      },
      {
        onSuccess: async () => {
          await digitalDeliveryCosistent.refetch();
          actions.close();
        },
        onError: async error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onAcceptAllDelivery = async () => {
    await acceptAllDigitalDelivery.mutateAsync(
      {
        productId,
      },
      {
        onSuccess: async () => {
          await digitalDeliveryCosistent.refetch();
        },
        onError: async error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onRejectAllDelivery = async (actions: PopupActions) => {
    await cancelAllDigitalDelivery.mutateAsync(
      {
        productId,
      },
      {
        onSuccess: async () => {
          await digitalDeliveryCosistent.refetch();
          actions.close();
        },
        onError: async error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const isWaitingForAccept =
    values.delivery?.status === DELIVERY_STATUS.waitingForAccept;

  const showReconnection =
    !isDeliveryMethodManual &&
    !isDeliveryCanceled &&
    !isDeliveryWithdrawn;

  const showYtContentMatchPolicy =
    !isDeliveryMethodManual &&
    !isDeliveryCanceled &&
    !isDeliveryWithdrawn &&
    !!values.delivery?.requireYtContentMatchPolicy;

  return (
    <TopbarPage
      bottombar={
        <DigitalDeliveryBottomBar
          showReconnection={showReconnection}
          showYtContentMatchPolicy={showYtContentMatchPolicy}
          ytContentMatchPolicyOptions={getSelectOptions(
            YT_CONTENT_MATCH_POLICY,
            'enum.YT_CONTENT_MATCH_POLICY',
          )}
          showManualStatus={
            isDeliveryMethodManual && !isDeliveryCanceled && !isDeliveryWithdrawn
          }
          statusOptions={getSelectOptions(
            DELIVERY_STATUS,
            'enum.DELIVERY_STATUS',
          )}
          isDeliveryCanceled={isDeliveryCanceled}
          isDeliveryWithdrawn={isDeliveryWithdrawn}
        />
      }
      topbar={
        <TopBar
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(`/music-product-digital/${productId}/delivery`)
              }>
                            <Button.Description description={t('button.back')} />

                            <Button.Icon icon={<PiCaretLeft />} />
            </Button>
          }
          title={
            digitalDeliveryCosistent.data?.insert
              ? t('button.publish-all')
              : t('button.change-all')
          }
          topbarContent={
            <div className='flex w-full items-center justify-end'>
              <div className='flex items-center gap-8 w-full justify-end font-medium'>
                {values.delivery?.deliveryType !== undefined &&
                  t(
                    `enum.DELIVERY_METHOD.${
                      DELIVERY_METHOD[values.delivery.deliveryType]
                    }`
                  )}
                {' - '}
                {values.delivery?.transferMethod !== undefined &&
                  t(
                    `enum.TRANSFER_METHOD.${Object.keys(TRANSFER_METHOD).find(
                      key =>
                        TRANSFER_METHOD[key as keyof typeof TRANSFER_METHOD] ===
                        values.delivery?.transferMethod
                    )}`
                  )}
                {values.delivery?.waitForAssetId && (
                  <Label color='blue' size='compact' withText>
                    {t('input.label.waitForAssetId')}
                  </Label>
                )}
                {values.delivery?.status !== undefined &&
                  !isDeliveryMethodManual && (
                    <LabeledDeliveryStatus status={values.delivery.status} />
                  )}
                <div className='flex gap-2'>
                  {values.delivery?.hasDiscrepancy && (
                    <DeliveryDiscrepancyPopup
                      name={`${values.delivery?.salesChannelName} ${values.delivery?.providerName}`}
                    />
                  )}
                  {isWaitingForAccept ? (
                    <>
                      <Button
                        variant={BUTTON_VARIANT.submit}
                        onClick={onAcceptAllDelivery}
                        disabled={!values.delivery || acceptAllDigitalDelivery.isPending}
                        isLoading={acceptAllDigitalDelivery.isPending}>
                        <Button.Text text={t('button.accept')} />
                        <Button.Icon icon={<PiCheck />} />
                      </Button>
                      <Popup.CancelWarning
                        name={t('music-product-digital.delivery.all-services')}
                        transI18nKey={
                          'music-product-digital.delivery.cancel-all-warning'
                        }
                        onCancel={onRejectAllDelivery}
                        trigger={
                          <Button
                            variant={BUTTON_VARIANT.remove}
                            disabled={!values.delivery || cancelAllDigitalDelivery.isPending}
                            isLoading={cancelAllDigitalDelivery.isPending}>
                            <Button.Text text={t('button.reject')} />
                            <Button.Icon icon={<PiX />} />
                          </Button>
                        }
                      />
                    </>
                  ) : (
                    <>
                      {values.delivery?.status === DELIVERY_STATUS.waiting && (
                        <Popup.CancelWarning
                          name={t('music-product-digital.delivery.all-services')}
                          transI18nKey={
                            'music-product-digital.delivery.cancel-all-warning'
                          }
                          onCancel={onCancelDelivery}
                          trigger={
                            <Button
                              isClear
                              onlyIcon
                              variant={BUTTON_VARIANT.warning}>
                                                            <Button.Description description={t('button.cancel')} />

                                                            <Button.Icon icon={<PiXCircle />} />
                            </Button>
                          }
                        />
                      )}
                      {!isDeliveryCanceled &&
                        !isDeliveryWithdrawn &&
                        isDeliveryMethodManual && (
                          <Button
                            variant={BUTTON_VARIANT.submit}
                            onClick={() => onSubmitDelivery()}
                            disabled={!values.delivery}>
                            <Button.Text text={t('button.change')} />
                            <Button.Icon icon={<PiGlobeSimple />} />
                          </Button>
                        )}

                      {!isDeliveryWithdrawn && (
                        <Popup.CancelWarning
                          name={t('music-product-digital.delivery.all-services')}
                          transI18nKey={
                            values.delivery?.status === DELIVERY_STATUS.notSend ||
                            values.delivery?.status === undefined
                              ? 'music-product-digital.delivery.publish-all-warning'
                              : 'music-product-digital.delivery.change-all-warning'
                          }
                          onCancel={onSubmitDelivery}
                          trigger={
                            <Button
                              variant={BUTTON_VARIANT.submit}
                              disabled={!values.delivery}>
                              <Button.Text
                                text={
                                  values.delivery?.status ===
                                    DELIVERY_STATUS.notSend ||
                                  values.delivery?.status === undefined
                                    ? t('button.publish')
                                    : t('button.change')
                                }
                              />
                              <Button.Icon icon={<PiGlobeSimple />} />
                            </Button>
                          }
                        />
                      )}
                    </>
                  )}
                  {!isDeliveryCanceled &&
                    !isDeliveryWithdrawn &&
                    !isDeliveryMethodManual &&
                    values.delivery?.status !== DELIVERY_STATUS.notSend &&
                    values.delivery?.status !== undefined && (
                      <>
                        <DropdownMenu
                          ref={cancelVisible.ref}
                          visible={cancelVisible.isComponentVisible}
                          style={{ padding: 5 }}
                          justifyPosition={'end'}>
                          <div className='flex flex-col w-full'>
                            <Popup.CancelWarning
                              name={t(
                                'music-product-digital.delivery.all-services'
                              )}
                              transI18nKey={
                                'music-product-digital.delivery.withdrawn-all-warning'
                              }
                              onCancel={actions =>
                                onWithdrawnDelivery(actions, false)
                              }
                              trigger={
                                <Button isClear fullWidth>
                                  <Button.Text text={t('button.withdrawn')} />
                                </Button>
                              }
                            />
                            <Popup.CancelWarning
                              name={t(
                                'music-product-digital.delivery.all-services'
                              )}
                              transI18nKey={
                                'music-product-digital.delivery.withdrawn-all-warning'
                              }
                              onCancel={actions =>
                                onWithdrawnDelivery(actions, true)
                              }
                              trigger={
                                <Button isClear fullWidth>
                                  <Button.Text
                                    text={t('button.withdrawn-priority')}
                                  />
                                </Button>
                              }
                            />
                          </div>
                        </DropdownMenu>
                        <Button
                          variant={BUTTON_VARIANT.default}
                          onlyIcon
                          isClear
                          disabled={!digitalDeliveryCosistent.data}
                          onClick={cancelVisible.handleComponentVisible}>
                                                    <Button.Description description={t('button.more-options')} />

                                                    <Button.Icon icon={<PiDotsThreeVerticalBold />} />
                        </Button>
                      </>
                    )}
                </div>
              </div>
            </div>
          }
        />
      }>
      {!isDeliveryMethodManual && (
        <div className='flex flex-col-reverse h-full w-full gap-4 justify-end mb-16'>
          <div className='flex w-full '>
            <Table
              id={'MusicProductDigitalDeliveryAll'}
              size='compact'
              overflowHidden={false}
              data={getDeliveryNodes(values.delivery?.excluded)}
              columns={[
                {
                  label: t(`music-product-digital.delivery.excluded-country`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].countryCode`}
                        label={t('input.label.country')}
                        options={
                          countries
                            ? [
                                {
                                  label: t(
                                    'music-product-digital.delivery.all'
                                  ),
                                  value: 'ALL',
                                },
                                ...countries.map(country => ({
                                  label: t(`countries.${country.codeAlfa2}`),
                                  value: country.codeAlfa2,
                                })),
                              ]
                            : []
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.subscriptionType`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].subscriptionTypeId`}
                        label={t('input.label.subscriptionType')}
                        options={getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE'
                        )}
                        onChange={value => {
                          setFieldValue(
                            `delivery.excluded[${id}].subscriptionTypeId`,
                            value.value
                          );
                        }}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.asap`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].asap`}
                        label={t('input.label.asap')}
                        options={getSelectOptions(AsapType, 'enum.AsapType')}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.timeZoneBased`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].isTimeZoneBased`}
                        label={t('input.label.timeZoneBased')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <Input.ComplexWrapper
                        sizes={[60, 40]}
                        className='max-w-72'>
                        <FormInput.Text
                          name={`delivery.excluded[${id}].publishDate`}
                          type='date'
                          min={minPublishDate}
                          displayMode={false}
                          disabled={
                            values.delivery?.excluded[id].asap !== AsapType.None
                          }
                        />
                        <FormInput.Text
                          name={`delivery.excluded[${id}].publishTime`}
                          type='time'
                          displayMode={false}
                          disabled={
                            values.delivery?.excluded[id].asap !== AsapType.None
                          }
                        />
                      </Input.ComplexWrapper>
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: '',
                  renderCell: ({ id }) => {
                    return (
                      <TableItem justifyPosition='end'>
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          onClick={() =>
                            onRemoveDigitalDelivery(id, 'excluded')
                          }
                          isClear>
                                                    <Button.Description description={t('button.remove')} />

                                                    <Button.Icon icon={<PiTrash />} />
                        </Button>
                      </TableItem>
                    );
                  },
                },
              ]}
              tableOptions={{
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={() => onAddDigitalDelivery('excluded')}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }}
            />
          </div>
          <div className='flex w-full'>
            <Table
              id={'MusicProductDigitalDeliveryAll'}
              size='compact'
              overflowHidden={false}
              data={getDeliveryNodes(values.delivery?.included)}
              columns={[
                {
                  label: t(`music-product-digital.delivery.included-country`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].countryCode`}
                        label={t('input.label.country')}
                        options={
                          countries
                            ? [
                                {
                                  label: t(
                                    'music-product-digital.delivery.all'
                                  ),
                                  value: 'ALL',
                                },
                                ...countries.map(country => ({
                                  label: t(`countries.${country.codeAlfa2}`),
                                  value: country.codeAlfa2,
                                })),
                              ]
                            : []
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.subscriptionType`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].subscriptionTypeId`}
                        label={t('input.label.subscriptionType')}
                        options={getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE'
                        )}
                        onChange={value => {
                          setFieldValue(
                            `delivery.included[${id}].subscriptionTypeId`,
                            value.value
                          );
                        }}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.asap`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].asap`}
                        label={t('input.label.asap')}
                        options={getSelectOptions(AsapType, 'enum.AsapType')}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.timeZoneBased`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].isTimeZoneBased`}
                        label={t('input.label.timeZoneBased')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <Input.ComplexWrapper
                        sizes={[60, 40]}
                        className='max-w-72'>
                        <FormInput.Text
                          name={`delivery.included[${id}].publishDate`}
                          type='date'
                          min={minPublishDate}
                          displayMode={false}
                          disabled={
                            values.delivery?.included[id].asap !== AsapType.None
                          }
                        />
                        <FormInput.Text
                          name={`delivery.included[${id}].publishTime`}
                          type='time'
                          displayMode={false}
                          onChange={e => {
                            let value = e.target.value;
                            if (value && value.length === 5) {
                              value = `${value}:00`;
                            }
                            e.target.value = value;
                            e.persist();

                            const originalOnChange = e.target.onChange;
                            if (originalOnChange) originalOnChange(e);
                          }}
                          disabled={
                            values.delivery?.included[id].asap !== AsapType.None
                          }
                        />
                      </Input.ComplexWrapper>
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: '',
                  renderCell: ({ id }) => {
                    return (
                      <TableItem justifyPosition='end'>
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          onClick={() =>
                            onRemoveDigitalDelivery(id, 'included')
                          }
                          isClear>
                                                    <Button.Description description={t('button.remove')} />

                                                    <Button.Icon icon={<PiTrash />} />
                        </Button>
                      </TableItem>
                    );
                  },
                },
              ]}
              tableOptions={{
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={() => onAddDigitalDelivery('included')}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }}
            />
          </div>
        </div>
      )}
    </TopbarPage>
  );
};

export default MusicProductDigitalDeliveryAll;
